import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';

interface SubscriptionDetails {
  has_access: boolean;
  status: string | null;
  plan_id: string | null;
  plan_name: string | null;
  billing_interval: string | null;
  amount: number | null;
  currency: string | null;
  features: Record<string, any>;
  max_team_members: number | null;
  max_businesses: number | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  quantity: number;
  trial_end: string | null;
}

interface SubscriptionStatus {
  hasAccess: boolean;
  hasSubscription: boolean;
  status: string | null;
  planName: string | null;
  features: Record<string, any>;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  isVerifying: boolean;
  hasVerified: boolean;
}

export const useSubscription = (teamId?: string) => {
  const { user } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const verifySubscription = async (teamId: string) => {
    try {
      setVerifying(true);
      console.log('Verifying potential subscription for team:', teamId);

      const { data, error } = await supabase.functions.invoke('verify-team-subscription', {
        body: { teamId }
      });

      if (error) throw error;

      if (data?.success) {
        console.log('Subscription verification successful:', data);
        // Refresh subscription status after verification
        await checkSubscription();
        return true;
      }

      return false;
    } catch (err) {
      console.error('Error verifying subscription:', err);
      return false;
    } finally {
      setVerifying(false);
    }
  };

  const checkSubscription = async () => {
    if (!user || !teamId) {
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Checking subscription for team:', teamId);

      // Use the new database function to get subscription details
      const { data, error } = await supabase.rpc('get_team_subscription_details', {
        p_team_id: teamId
      });

      if (error) throw error;

      const details = data as SubscriptionDetails;
      const now = new Date();

      // Calculate trial status
      const isTrialActive = !!details.trial_end && new Date(details.trial_end) > now;
      const trialDaysRemaining = details.trial_end && isTrialActive
        ? Math.ceil((new Date(details.trial_end).getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        : null;

      const status: SubscriptionStatus = {
        hasAccess: details.has_access,
        hasSubscription: details.status === 'active' || details.status === 'trialing' || details.status === 'internal',
        status: details.status,
        planName: details.plan_name,
        features: details.features || {},
        currentPeriodEnd: details.current_period_end,
        cancelAtPeriodEnd: details.cancel_at_period_end || false,
        trialEnd: details.trial_end,
        isTrialActive,
        trialDaysRemaining,
        isVerifying: false,
        hasVerified: false
      };

      console.log('Subscription status result:', status);
      setSubscriptionStatus(status);
      setError(null);

      // Auto-verify if team has no subscription and user might have paid
      if (!details.has_access && !verifying) {
        console.log('No subscription found, attempting auto-verification...');
        const verified = await verifySubscription(teamId);
        if (verified) {
          status.hasVerified = true;
        }
      }

    } catch (err) {
      console.error('Error checking subscription:', err);
      setError('Failed to check subscription status');
      setSubscriptionStatus({
        hasAccess: false,
        hasSubscription: false,
        status: null,
        planName: null,
        features: {},
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        trialEnd: null,
        isTrialActive: false,
        trialDaysRemaining: null,
        isVerifying: false,
        hasVerified: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset subscription state immediately on team change to prevent stale UI
    if (user && teamId) {
      setSubscriptionStatus(null);
      setError(null);
      setLoading(true);
    }
    checkSubscription();
  }, [user, teamId]);

  return {
    subscriptionStatus,
    loading: loading || verifying,
    error,
    refetch: checkSubscription,
    verifying,
  };
};
